<template>
    <DxFileManager
        ref="fileManagerRef"
		:file-system-provider="fileSystemProvider"
		:current-path="currentPath"
		:customize-detail-columns="customizeDetailColumns"
		:on-selected-file-opened="onFileDoubleClick"
		height="100%"
		@current-path-changed="onCurrentPathChanged"
		@context-menu-item-click="onContextMenuItemClick"
	>
		<DxPermissions :create="true" :copy="true" :move="true" :delete="true" :rename="true" :upload="true" :download="true" />
		<DxUpload :chunk-size="1024*1024*1000" :max-file-size="1024*1024*50" />
        <DxToolbar>
            <DxItem name="showNavPane" />
            <DxItem name="create" />
            <DxItem name="upload" />
            <DxItem name="switchView" />
            <DxItem name="separator" location="after" />
			<DxItem
                widget="dxButton"
                location="after"
                locate-in-menu="never"
                :options="{ text: 'Notetaking', icon: 'edit', onClick: openNotePopup }"
            />
            <DxItem name="refresh" />
			<DxFileSelectionItem name="view" widget="dxButton" :options="{ text: 'View', icon: 'eyeopen', onClick: handleViewToolbarClick }" />
			<DxFileSelectionItem name="download" />
			<DxFileSelectionItem name="separator" />
			<DxFileSelectionItem name="move" />
			<DxFileSelectionItem name="copy" />
			<DxFileSelectionItem name="rename" />
			<DxFileSelectionItem name="separator" />
			<DxFileSelectionItem name="delete" />
			<DxFileSelectionItem name="clearSelection" />
			<DxFileSelectionItem name="separator" location="after" />
			<DxFileSelectionItem name="refresh" />
        </DxToolbar>
		<DxContextMenu :on-showing="onContextMenuShowing">
			<DxItem name="create" />
			<DxItem name="upload" />
			<DxItem name="rename" />
			<DxItem name="move" />
			<DxItem name="copy" />
			<DxItem name="delete" />
			<DxItem name="refresh" />
			<DxItem name="view" text="View" icon="eyeopen" />
			<DxItem name="download" />
			<DxItem name="permissions" text="Permissions" icon="unlock" />
			<DxItem name="verify_ai" text="Verify AI Parsing" icon="check" />
		</DxContextMenu>
		<DxPopup :visible="permissionsPopupVisible" :show-title="true" :title="permissionsPopupTitle" :drag-enabled="true" :hide-on-outside-click="false" :width="720" height="auto" @hiding="onPermissionsPopupHiding">
			<template #content>
				<div style="padding: 8px;">
					<permissions-editor v-model="permissionsModel" type="drive" />
					<div style="display:flex;justify-content:flex-end;gap:8px;margin-top:12px;">
						<DxButton text="Cancel" styling-mode="outlined" @click="closePermissionsPopup" />
						<DxButton :disabled="isSavingPermissions" type="default" text="Save" @click="savePermissions" />
					</div>
				</div>
			</template>
		</DxPopup>
        <DxPopup :visible="notePopupVisible" :show-title="true" title="Notetaking" :drag-enabled="true" :resize-enabled="true" :hide-on-outside-click="false" :width="520" :min-width="400" :min-height="330" height="auto" @hiding="onNotePopupHiding">
            <template #content>
                <div style="padding: 8px; display: flex; flex-direction: column; gap: 8px; height: 100%; box-sizing: border-box;">
                    <div style="flex: 1 1 auto; min-height: 160px; display: flex;">
                        <DxTextArea
                            v-model:value="noteContent"
                            label="Note Contents"
                            label-mode="floating"
                            :auto-resize-enabled="false"
                            width="100%"
                            height="100%"
                        />
                    </div>
                    <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:12px;">
                        <div style="font-size:12px;color:var(--dx-color-text-secondary, rgba(0,0,0,0.6));">
                            Notes will be sent to AI services and may be queried.
                        </div>
                        <div style="display:flex;gap:8px;">
                            <DxButton text="Cancel" styling-mode="outlined" @click="closeNotePopup" />
                            <DxButton :disabled="isSavingNote" type="default" text="Save" @click="saveNote" />
                        </div>
                    </div>
                </div>
            </template>
        </DxPopup>
		<DxPopup :visible="aiVerifierPopupVisible" :show-title="true" :title="aiVerifierPopupTitle" :drag-enabled="true" :resize-enabled="true" :hide-on-outside-click="false" width="90vw" height="90vh" @hiding="onAiVerifierPopupHiding">
			<template #content>
				<div style="padding: 8px;">
					<ai-verifier
						:file-id="aiVerifierFileId"
						:file-name="aiVerifierFileName"
						:image-url="aiVerifierImageUrl"
						v-model:content="aiVerifierContent"
					/>
					<div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
						<DxButton text="Cancel" styling-mode="outlined" @click="closeAiVerifierPopup" />
						<DxButton text="Reject" type="danger" styling-mode="outlined" @click="rejectAiParsing" :disabled="isSavingAiVerification" />
						<DxButton text="Approve" type="success" @click="approveAiParsing" :disabled="isSavingAiVerification" />
					</div>
				</div>
			</template>
		</DxPopup>
		<template #columnHasPermissionsRender="{ data }">{{ Array.isArray(data.data.fileItem.dataItem.permissions) && data.data.fileItem.dataItem.permissions.length > 0 ? '🔒' : '' }}</template>
		<template #columnAiStateRender="{ data }">
			<i
				v-if="data.data.fileItem.dataItem.aistate === 'intend' || data.data.fileItem.dataItem.aistate ==='processing' || data.data.fileItem.dataItem.aistate === 'complete_verification'"
				class="dx-icon-runner"
				style="color: #2196f3; font-size: 18px;"
				title="Processing"
			></i>
			<i
				v-else-if="data.data.fileItem.dataItem.aistate === 'pending_verification'"
				class="dx-icon-warning"
				style="color: #f5a623; font-size: 18px;"
				title="Pending Verification"
			></i>
			<i
				v-else-if="data.data.fileItem.dataItem.aistate === 'success'"
				class="dx-icon-check"
				style="color: #4caf50; font-size: 18px;"
				title="Processed"
			></i>
			<i
				v-else-if="data.data.fileItem.dataItem.aistate === 'error'"
				class="dx-icon-warning"
				style="color: #f44336; font-size: 18px;"
				title="Error"
			></i>
		</template>
	</DxFileManager>
</template>

<script setup>
/* eslint-disable */
// A note about chunk-size: we do not support chunking files for now. But DxFileManager does not allow unchunked uploads like DxFileUploader,
// so we simply set it to a very large value and warn if we detect multiple chunks.

import { defineProps, ref, inject, computed } from 'vue';
import { DxFileManager, DxPermissions, DxUpload, DxContextMenu, DxItem, DxToolbar, DxFileSelectionItem } from 'devextreme-vue/file-manager';
import DxPopup from 'devextreme-vue/popup';
import DxButton from 'devextreme-vue/button';
import DxTextArea from 'devextreme-vue/text-area';
import permissionsEditor from '../permissions-editor.vue';
import aiVerifier from '../ai-verifier.vue';
import CustomFileSystemProvider from 'devextreme/file_management/custom_provider';
import { alert as dxAlert, confirm as dxConfirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';

const props = defineProps({
	preview: Boolean,
});
const $http = inject('$http');
console.log(props);

const currentPath = ref('');
const fileManagerRef = ref(null);

// Image file extensions for detecting image uploads
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.ico', '.tiff', '.tif'];

// Document file extensions that can be viewed in browser
const DOCUMENT_EXTENSIONS = ['.pdf', '.txt', '.html', '.htm', '.xml', '.csv'];

// Office document extensions (some browsers can view these)
const OFFICE_DOCUMENT_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];

// Helper to check if a filename has an image extension
function isImageFile(filename) {
	if (!filename || typeof filename !== 'string') return false;
	const lower = filename.toLowerCase();
	return IMAGE_EXTENSIONS.some(ext => lower.endsWith(ext));
}

// Helper to check if a file can be viewed in browser
function isViewableFile(filename) {
	if (!filename || typeof filename !== 'string') return false;
	const lower = filename.toLowerCase();
	return IMAGE_EXTENSIONS.some(ext => lower.endsWith(ext)) ||
		   DOCUMENT_EXTENSIONS.some(ext => lower.endsWith(ext)) ||
		   OFFICE_DOCUMENT_EXTENSIONS.some(ext => lower.endsWith(ext));
}

// Schedule delayed refreshes at 5s, 10s, and 20s after certain operations
function scheduleDelayedRefreshes() {
	const delays = [5000, 10000, 20000];
	delays.forEach(delay => {
		setTimeout(() => {
			if (fileManagerRef.value && fileManagerRef.value.instance && typeof fileManagerRef.value.instance.refresh === 'function') {
				fileManagerRef.value.instance.refresh();
			}
		}, delay);
	});
}

const fileSystemProvider = new CustomFileSystemProvider({
	keyExpr: 'id',
	isDirectoryExpr: 'is_directory',
	dateModifiedExpr: 'updated_at',
	hasSubDirectoriesExpr: 'has_subdir',
	getItems: async (parentDirectory) => {
		let fetchUrl = (parentDirectory && parentDirectory.key > 0) ? `/filetree/${parentDirectory['key']}` : '/filetree';
		let fetchRequest = await $http.get(fetchUrl);
		return fetchRequest.data;
	},

	downloadItems: async (items) => {
		try {
			if (!Array.isArray(items) || items.length === 0) {
				return;
			}

			for (const item of items) {
				// Skip directories (backend download expected for files)
				if (item && (item.isDirectory === true || item.dataItem?.is_directory === true)) {
					notify({ message: `Cannot download folder: ${item.name}`, type: 'warning', duration: 3000, position: 'top right' });
					continue;
				}

				const id = (item && item.dataItem && item.dataItem.id) ? item.dataItem.id : item?.key;
				if (!id) {
					console.error('downloadItems: Missing item id', item);
					notify({ message: 'Unable to download: missing file identifier.', type: 'error', duration: 4000, position: 'top right' });
					continue;
				}

				let filename = item?.name || 'download';

				// Always use download parameter to get file content (backend requires it)
				const response = await $http.get(`/filetree/${id}`, {
					params: { download: 1 },
					responseType: 'blob',
				});

				const blob = response?.data;
				if (!blob) {
					notify({ message: 'Failed to download file.', type: 'error', duration: 4000, position: 'top right' });
					continue;
				}

				// Extract filename from Content-Disposition header if available
				const disposition = response?.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition']);
				if (disposition && typeof disposition === 'string') {
					const match = disposition.match(/filename\*=UTF-8''([^;\n]+)|filename="?([^";\n]+)"?/i);
					const extracted = match ? (decodeURIComponent(match[1] || match[2] || '')) : '';
					if (extracted) filename = extracted;
				}

				// Always download (trigger actual file download)
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				setTimeout(() => {
					window.URL.revokeObjectURL(url);
					if (link.parentNode) {
						link.parentNode.removeChild(link);
					}
				}, 0);
			}

			return;
		} catch (error) {
			console.error('Error downloading items:', error);
			notify({ message: 'Error downloading file(s).', type: 'error', duration: 5000, position: 'top right' });
			throw error;
		}
	},
	createDirectory: async (parentDirectory, name) => {
		try {
			const response = await $http.post('/filetree', {
				name: name,
				is_directory: true,
				parent_id: parentDirectory['key'] || null
			});
			console.log("Directory created:", response.data);
			return;
		} catch (error) {
			console.error("Error creating directory:", error);
			throw error;
		}
	},
	async uploadFileChunk(fileData, uploadInfo, destinationDirectory) {
		if (uploadInfo.chunkCount > 1) {
			notify({ message: "Chunked uploads are not supported. Please use a single chunk when uploading via File Manager.", type: 'error', duration: 5000, position: 'top right' });
			console.error("Chunked uploads are not supported. Please use a single chunk when uploading via File Manager.");
			throw new Error("Chunked uploads are not supported. Please use a single chunk when uploading via File Manager.");
		}
		const parentId = (destinationDirectory && destinationDirectory['key']) ? destinationDirectory['key'] : null;
		const fileName = (uploadInfo && uploadInfo.fileName) ? uploadInfo.fileName : (fileData && fileData.name) ? fileData.name : 'upload';
		// Do not allow uploads to the root directory.
		if (parentId === null || parentId === undefined) {
			dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">Cannot upload file to root directory</div></div>`, `Sorry, you are not allowed to upload files to the root drive. Please create a folder, or upload to an existing folder.`);
			throw new Error("Cannot upload to root directory.");
		}
		
		// Prompt user whether to use AI for this file
		const useAI = await dxConfirm(
			`Do you want to enable AI processing for the file <b>${fileName}</b>?<br><br>AI processing allows the file to be queried and analyzed by AI services.`,
			'Enable AI Processing?'
		);
		
		const formData = new FormData();
		formData.append('name', fileName);
		if (parentId !== null && parentId !== undefined) {
			formData.append('parent_id', String(parentId));
		}
		formData.append('is_directory', '0');		// Use 0 instead of 'false' for Laravel.
		formData.append('require_ai', useAI ? '1' : '0');
		formData.append('file', fileData, fileName);
		
		return $http.post('/filetree', formData)
			.then(() => {
				notify({ message: "File uploaded successfully.", type: 'success', duration: 3000, position: 'top right' });
				// Schedule delayed refreshes for image uploads (AI processing may update state)
				if (isImageFile(fileName)) {
					scheduleDelayedRefreshes();
				}
				return;
			})
			.catch((error) => {
				console.error("Error uploading file:", error);
				notify({ message: "Error uploading file.", type: 'error', duration: 5000, position: 'top right' });
				throw error;
			});
	},
	deleteItem: async (item) => {
		try {
			const id = (item && item.dataItem && item.dataItem.id) ? item.dataItem.id : item?.key;
			if (!id) {
				console.error('deleteItem: Missing item id', item);
				notify({ message: 'Unable to delete: missing item identifier.', type: 'error', duration: 4000, position: 'top right' });
				throw new Error('Missing item id');
			}

			await $http.delete(`/filetree/${id}`);
			notify({ message: 'Item deleted successfully.', type: 'success', duration: 3000, position: 'top right' });
			return;
		} catch (error) {
			const status = error && error.response && error.response.status;
			if (status === 422) {
				let backendMessage = '';
				const data = error.response && error.response.data;
				if (data) {
					if (typeof data.message === 'string') {
						backendMessage = data.message;
					} else if (data.errors && typeof data.errors === 'object') {
						const firstKey = Object.keys(data.errors)[0];
						const firstVal = firstKey ? data.errors[firstKey] : null;
						backendMessage = Array.isArray(firstVal) ? firstVal[0] : String(firstVal || 'Validation error');
					} else if (typeof data === 'string') {
						backendMessage = data;
					}
				}
				backendMessage = backendMessage || 'Validation error';
				console.error('Error deleting item (422):', backendMessage, error);
				notify({ message: backendMessage, type: 'error', duration: 5000, position: 'top right' });
			} else {
				console.error('Error deleting item:', error);
				notify({ message: 'Error deleting item.', type: 'error', duration: 5000, position: 'top right' });
			}
			throw error;
		}
	},
	renameItem: async (item, newName) => {
		try {
			const id = (item && item.dataItem && item.dataItem.id) ? item.dataItem.id : item?.key;
			if (!id) {
				console.error('renameItem: Missing item id', item);
				notify({ message: 'Unable to rename: missing item identifier.', type: 'error', duration: 4000, position: 'top right' });
				throw new Error('Missing item id');
			}

			const name = (typeof newName === 'string') ? newName.trim() : '';
			if (!name) {
				notify({ message: 'New name is required.', type: 'warning', duration: 3000, position: 'top right' });
				throw new Error('Missing new name');
			}

			await $http.patch(`/filetree/${id}`, { name });
			if (item && item.dataItem) {
				item.dataItem.name = name;
			}
			if (item && typeof item.name === 'string') {
				item.name = name;
			}
			notify({ message: 'Item renamed successfully.', type: 'success', duration: 3000, position: 'top right' });
			return;
		} catch (error) {
			const status = error && error.response && error.response.status;
			if (status === 422) {
				let backendMessage = '';
				const data = error.response && error.response.data;
				if (data) {
					if (typeof data.message === 'string') {
						backendMessage = data.message;
					} else if (data.errors && typeof data.errors === 'object') {
						const firstKey = Object.keys(data.errors)[0];
						const firstVal = firstKey ? data.errors[firstKey] : null;
						backendMessage = Array.isArray(firstVal) ? firstVal[0] : String(firstVal || 'Validation error');
					} else if (typeof data === 'string') {
						backendMessage = data;
					}
				}
				backendMessage = backendMessage || 'Validation error';
				console.error('Error renaming item (422):', backendMessage, error);
				notify({ message: backendMessage, type: 'error', duration: 5000, position: 'top right' });
			} else {
				console.error('Error renaming item:', error);
				notify({ message: 'Error renaming item.', type: 'error', duration: 5000, position: 'top right' });
			}
			throw error;
		}
	},
	copyItem: async (item, destinationDirectory) => {
		console.log('copyItem', item, destinationDirectory);
		throw new Error('Not implemented');
	},
	moveItem: async (item, destinationDirectory) => {
		console.log('moveItem', item, destinationDirectory);
		throw new Error('Not implemented');
	},
});

const onCurrentPathChanged = (e) => {
	currentPath.value = e.component.option('currentPath');
};

// Handle double-click on files (not folders) to invoke View
const onFileDoubleClick = (e) => {
	try {
		const fsItem = e && e.file;
		if (!fsItem) {
			return;
		}
		
		// Only handle files, not folders
		if (!fsItem.isDirectory) {
			viewFileInBrowser(fsItem);
		}
		// For folders, allow default navigation behavior
	} catch (error) {
		console.error('Error handling file double-click:', error);
		notify({ message: 'Error opening file.', type: 'error', duration: 4000, position: 'top right' });
	}
};

// Function to view files in browser
async function viewFileInBrowser(fsItem) {
	try {
		// Skip directories
		if (fsItem && (fsItem.isDirectory === true || fsItem.dataItem?.is_directory === true)) {
			notify({ message: `Cannot view folder: ${fsItem.name}`, type: 'warning', duration: 3000, position: 'top right' });
			return;
		}

		const id = (fsItem && fsItem.dataItem && fsItem.dataItem.id) ? fsItem.dataItem.id : fsItem?.key;
		if (!id) {
			console.error('viewFileInBrowser: Missing item id', fsItem);
			notify({ message: 'Unable to view: missing file identifier.', type: 'error', duration: 4000, position: 'top right' });
			return;
		}

		let filename = fsItem?.name || 'file';

		// Fetch file content with download parameter (backend requires it)
		const response = await $http.get(`/filetree/${id}`, {
			params: { download: 1 },
			responseType: 'blob',
		});

		const blob = response?.data;
		if (!blob) {
			notify({ message: 'Failed to load file for viewing.', type: 'error', duration: 4000, position: 'top right' });
			return;
		}

		// Extract filename from Content-Disposition header if available
		const disposition = response?.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition']);
		if (disposition && typeof disposition === 'string') {
			const match = disposition.match(/filename\*=UTF-8''([^;\n]+)|filename="?([^";\n]+)"?/i);
			const extracted = match ? (decodeURIComponent(match[1] || match[2] || '')) : '';
			if (extracted) filename = extracted;
		}

		// Get Content-Type from response headers to set correct MIME type for blob
		const contentType = response?.headers && (response.headers['content-type'] || response.headers['Content-Type']);
		
		// Create blob with correct MIME type for proper browser display
		const typedBlob = contentType ? new Blob([blob], { type: contentType }) : blob;
		const url = window.URL.createObjectURL(typedBlob);
		const newWindow = window.open(url, '_blank');
		
		if (!newWindow) {
			// If popup blocked, fall back to download
			notify({ message: 'Popup blocked. Please allow popups to view files, or use download instead.', type: 'warning', duration: 4000, position: 'top right' });
			const link = document.createElement('a');
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			setTimeout(() => {
				window.URL.revokeObjectURL(url);
				if (link.parentNode) {
					link.parentNode.removeChild(link);
				}
			}, 0);
		} else {
			// Revoke URL after a delay to allow the browser to load it
			// Note: blob URLs are temporary, so "Save As" won't work well from the viewer
			// For better "Save As" support, consider backend changes to serve files with inline disposition
			setTimeout(() => {
				window.URL.revokeObjectURL(url);
			}, 1000);
		}
	} catch (error) {
		console.error('Error viewing file:', error);
		notify({ message: 'Error viewing file.', type: 'error', duration: 5000, position: 'top right' });
	}
}

// Handle toolbar view button click
function handleViewToolbarClick() {
	try {
		if (!fileManagerRef.value || !fileManagerRef.value.instance) {
			notify({ message: 'File manager not available.', type: 'warning', duration: 3000, position: 'top right' });
			return;
		}
		
		const selectedItems = fileManagerRef.value.instance.getSelectedItems();
		if (!selectedItems || selectedItems.length === 0) {
			notify({ message: 'Please select a file to view.', type: 'warning', duration: 3000, position: 'top right' });
			return;
		}
		
		// View the first selected item
		const firstItem = selectedItems[0];
		viewFileInBrowser(firstItem);
	} catch (error) {
		console.error('Error handling view toolbar click:', error);
		notify({ message: 'Error viewing file.', type: 'error', duration: 5000, position: 'top right' });
	}
}

// Handle new context menu clicks for permissions in file manager.
const onContextMenuItemClick = (e) => {
	try {
		const clickedName = e && e.itemData && (e.itemData.name || e.itemData.text);
		if (clickedName === 'permissions') {
			const fsItem = e && e.fileSystemItem;
			console.log('Permissions edit request', fsItem);
			openPermissionsPopup(fsItem);
		} else if (clickedName === 'verify_ai') {
			const fsItem = e && e.fileSystemItem;
			console.log('AI verification request', fsItem);
			openAiVerifierPopup(fsItem);
		} else if (clickedName === 'view') {
			const fsItem = e && e.fileSystemItem;
			console.log('View file request', fsItem);
			viewFileInBrowser(fsItem);
		}
	} catch (error) {
		console.error('Context menu action failed:', error);
		notify({ message: 'Action failed.', type: 'error', duration: 4000, position: 'top right' });
	}
};

// Handle context menu showing to conditionally show/hide items
// TODO: Doesn't work! We can't hide menu items this way; e.items here is always undefined.
const onContextMenuShowing = (e) => {
	if (!e) return;
	const fileItem = e.fileSystemItem;
	const isFile = fileItem && !fileItem.isDirectory;
	const aiState = fileItem && fileItem.dataItem && fileItem.dataItem.aistate;
	const showVerifyAi = isFile && aiState === 'pending_verification';
	const fileName = fileItem && (fileItem.name || (fileItem.dataItem && fileItem.dataItem.name));
	const showView = isFile && fileName && isViewableFile(fileName);
	
	console.log('onContextMenuShowing', e);
	// The onShowing event provides items directly in e.items array
	if (Array.isArray(e.items)) {
		e.items.forEach((item) => {
			if (item && item.name === 'verify_ai') {
				item.visible = showVerifyAi;
			}
			if (item && item.name === 'view') {
				item.visible = showView;
			}
		});
	}
};

// Handle changes to custom detail columns for permissions in file manager.
const customizeDetailColumns = (columns) => {
	// Add permissions column if not already present
	let existingPermIndex = columns.findIndex(c => c.dataField === 'fileItem.hasPermissions');
	if (existingPermIndex === -1) {
		let hasPermissionsColumn = {
			dataField: 'fileItem.hasPermissions',
			caption: '',
			editEnabled: false,
			cellTemplate: 'columnHasPermissionsRender',
			hidingPriority: 0,
			width: 54,
		};
		columns.push(hasPermissionsColumn);
	}
	
	// Add AI state column if not already present
	let existingAiIndex = columns.findIndex(c => c.dataField === 'fileItem.aistate');
	if (existingAiIndex === -1) {
		let aiStateColumn = {
			dataField: 'fileItem.aistate',
			caption: '',
			editEnabled: false,
			cellTemplate: 'columnAiStateRender',
			hidingPriority: 1,
			width: 54,
			alignment: 'center',
		};
		columns.push(aiStateColumn);
	}
	
	return columns;
};

// Popup state for permissions editor (stubbed load/save)
const permissionsPopupVisible = ref(false);
const selectedItemForPermissions = ref(null);
const permissionsModel = ref([]);
const isLoadingPermissions = ref(false);
const isSavingPermissions = ref(false);

const permissionsPopupTitle = computed(() => {
	const base = 'Edit permissions';
	const name = selectedItemForPermissions.value && selectedItemForPermissions.value.dataItem && selectedItemForPermissions.value.dataItem.name ? selectedItemForPermissions.value.dataItem.name : null;
	return name ? `${base} - ${name}` : base;
});

function openPermissionsPopup(fsItem) {
	selectedItemForPermissions.value = fsItem || null;
	// Load permissions directly from the selected item's data
	const perms = (fsItem && fsItem.dataItem && Array.isArray(fsItem.dataItem.permissions)) ? fsItem.dataItem.permissions : [];
	permissionsModel.value = Array.isArray(perms) ? [...perms] : [];
	isLoadingPermissions.value = false;
	permissionsPopupVisible.value = true;
}

function closePermissionsPopup() {
	permissionsPopupVisible.value = false;
}

function onPermissionsPopupHiding() {
	permissionsPopupVisible.value = false;
}

async function savePermissions() {
	try {
		isSavingPermissions.value = true;
		const fsNode = selectedItemForPermissions.value;
		const id = (fsNode && fsNode.dataItem && fsNode.dataItem.id) ? fsNode.dataItem.id : fsNode?.key;
		if (!id) {
			notify({ message: 'Unable to save: missing item identifier.', type: 'error', duration: 4000, position: 'top right' });
			return;
		}
		const payload = { permissions: Array.isArray(permissionsModel.value) ? permissionsModel.value : [] };
		await $http.patch(`/filetree/${id}`, payload);
		notify({ message: 'Permissions saved.', type: 'success', duration: 2000, position: 'top right' });
		if (fsNode && fsNode.dataItem) {
			fsNode.dataItem.permissions = payload.permissions;
		}
		closePermissionsPopup();
	} catch (error) {
		console.error('Failed to save permissions:', error);
		notify({ message: 'Failed to save permissions.', type: 'error', duration: 3000, position: 'top right' });
	} finally {
		isSavingPermissions.value = false;
	}
}

// Note taking popup state and handlers
const notePopupVisible = ref(false);
const noteContent = ref('');
const isSavingNote = ref(false);
// Resolve or create a root-level "Notes" folder for saving notes
async function resolveNotesFolderId() {
    try {
        // Fetch root items
        const rootResponse = await $http.get('/filetree');
        const items = Array.isArray(rootResponse && rootResponse.data) ? rootResponse.data : [];
        // Look for existing folder named "Notes"
        const existing = items.find((it) => (it && (it.is_directory === true || it.is_directory === 1)) && String(it.name).toLowerCase() === 'notes');
        if (existing && (existing.id || existing.key)) {
            return existing.id || existing.key;
        }
        // Create it at root
        const createResponse = await $http.post('/filetree', {
            name: 'Notes',
            is_directory: true,
            parent_id: null,
        });
        // Prefer id from create response if available; otherwise refetch root and locate
        const created = createResponse && createResponse.data ? createResponse.data : null;
        if (created && (created.id || created.key)) {
            return created.id || created.key;
        }
        const verifyResponse = await $http.get('/filetree');
        const verifyItems = Array.isArray(verifyResponse && verifyResponse.data) ? verifyResponse.data : [];
        const notes = verifyItems.find((it) => (it && (it.is_directory === true || it.is_directory === 1)) && String(it.name).toLowerCase() === 'notes');
        if (notes && (notes.id || notes.key)) {
            return notes.id || notes.key;
        }
        throw new Error('Notes folder not found after creation');
    } catch (error) {
        console.error('Failed to resolve Notes folder id:', error);
        throw error;
    }
}

function openNotePopup() {
    noteContent.value = '';
    notePopupVisible.value = true;
}
function closeNotePopup() {
    notePopupVisible.value = false;
}
function onNotePopupHiding() {
    notePopupVisible.value = false;
}
async function saveNote() {
    try {
		if (!noteContent.value || noteContent.value.trim() === '') {
			notify({ message: 'Some text to save is required.', type: 'warning', duration: 3000, position: 'top right' });
			return;
		}
        isSavingNote.value = true;
        // Ensure we have a Notes folder id at root
        const notesFolderId = await resolveNotesFolderId();
        const content = String(noteContent.value || '');
        const timestamp = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const name = `note-${timestamp.getFullYear()}${pad(timestamp.getMonth()+1)}${pad(timestamp.getDate())}-${pad(timestamp.getHours())}${pad(timestamp.getMinutes())}${pad(timestamp.getSeconds())}.txt`;

        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const formData = new FormData();
        formData.append('name', name);
        formData.append('parent_id', String(notesFolderId));
        formData.append('is_directory', '0');
        formData.append('file', blob, name);

        await $http.post('/filetree', formData);
        notify({ message: 'Note saved to drive.', type: 'success', duration: 2500, position: 'top right' });
        closeNotePopup();
        // Refresh the FileManager to reflect the new file
        if (fileManagerRef.value && fileManagerRef.value.instance && typeof fileManagerRef.value.instance.refresh === 'function') {
            fileManagerRef.value.instance.refresh();
        }
    } catch (error) {
        console.error('Failed to save note:', error);
        notify({ message: 'Failed to save note.', type: 'error', duration: 4000, position: 'top right' });
    } finally {
        isSavingNote.value = false;
    }
}

// AI Verifier popup state and handlers
const aiVerifierPopupVisible = ref(false);
const selectedItemForAiVerifier = ref(null);
const aiVerifierContent = ref('');
const aiVerifierImageUrl = ref('');
const aiVerifierFileId = ref(null);
const aiVerifierFileName = ref('');
const isSavingAiVerification = ref(false);
const isLoadingAiVerification = ref(false);

const aiVerifierPopupTitle = computed(() => {
	const base = 'Verify AI Parsing';
	const name = aiVerifierFileName.value || null;
	return name ? `${base} - ${name}` : base;
});

async function openAiVerifierPopup(fsItem) {
	selectedItemForAiVerifier.value = fsItem || null;
	const dataItem = fsItem && fsItem.dataItem ? fsItem.dataItem : {};
	aiVerifierFileId.value = dataItem.id || fsItem?.key || null;
	aiVerifierFileName.value = dataItem.name || fsItem?.name || '';
	aiVerifierContent.value = '';
	aiVerifierImageUrl.value = '';
	aiVerifierPopupVisible.value = true;
	
	// Load AI parsing data from server
	if (aiVerifierFileId.value) {
		try {
			isLoadingAiVerification.value = true;
			const response = await $http.get(`/filetree/${aiVerifierFileId.value}/ai-parsing`);
			const data = response && response.data && response.data ? response.data : {};
			aiVerifierContent.value = data.text || '';
			aiVerifierImageUrl.value = data['_href'] || '';
		} catch (error) {
			console.error('Failed to load AI parsing data:', error);
			notify({ message: 'Failed to load AI parsing data.', type: 'warning', duration: 3000, position: 'top right' });
		} finally {
			isLoadingAiVerification.value = false;
		}
	}
}

function closeAiVerifierPopup() {
	aiVerifierPopupVisible.value = false;
}

function onAiVerifierPopupHiding() {
	aiVerifierPopupVisible.value = false;
}

async function approveAiParsing() {
	try {
		isSavingAiVerification.value = true;
		const id = aiVerifierFileId.value;
		if (!id) {
			notify({ message: 'Unable to approve: missing file identifier.', type: 'error', duration: 4000, position: 'top right' });
			return;
		}
		await $http.post(`/filetree/${id}/ai-parsing/approve`, {
			content: aiVerifierContent.value,
		});
		notify({ message: 'AI parsing approved.', type: 'success', duration: 2000, position: 'top right' });
		// Update the local item state
		if (selectedItemForAiVerifier.value && selectedItemForAiVerifier.value.dataItem) {
			selectedItemForAiVerifier.value.dataItem.aistate = 'complete_verification';
		}
		closeAiVerifierPopup();
		// Refresh to update the AI state icon
		if (fileManagerRef.value && fileManagerRef.value.instance && typeof fileManagerRef.value.instance.refresh === 'function') {
			fileManagerRef.value.instance.refresh();
		}
		// Schedule additional delayed refreshes to catch backend processing updates
		scheduleDelayedRefreshes();
	} catch (error) {
		console.error('Failed to approve AI parsing:', error);
		notify({ message: 'Failed to approve AI parsing.', type: 'error', duration: 3000, position: 'top right' });
	} finally {
		isSavingAiVerification.value = false;
	}
}

async function rejectAiParsing() {
	try {
		isSavingAiVerification.value = true;
		const id = aiVerifierFileId.value;
		if (!id) {
			notify({ message: 'Unable to reject: missing file identifier.', type: 'error', duration: 4000, position: 'top right' });
			return;
		}
		await $http.post(`/filetree/${id}/ai-parsing/reject`);
		notify({ message: 'AI parsing rejected. File will be marked as invalid; you may reupload or delete this file.', type: 'info', duration: 3000, position: 'top right' });
		// Update the local item state
		if (selectedItemForAiVerifier.value && selectedItemForAiVerifier.value.dataItem) {
			selectedItemForAiVerifier.value.dataItem.aistate = 'intend';
		}
		closeAiVerifierPopup();
		// Refresh to update the AI state icon
		if (fileManagerRef.value && fileManagerRef.value.instance && typeof fileManagerRef.value.instance.refresh === 'function') {
			fileManagerRef.value.instance.refresh();
		}
		// Schedule additional delayed refreshes to catch backend processing updates
		scheduleDelayedRefreshes();
	} catch (error) {
		console.error('Failed to reject AI parsing:', error);
		notify({ message: 'Failed to reject AI parsing.', type: 'error', duration: 3000, position: 'top right' });
	} finally {
		isSavingAiVerification.value = false;
	}
}
</script>
